// Type-safe references to every art asset in public/art/.
// Import the helpers, not raw strings, so asset paths are refactored
// in one place when the directory structure changes.

// ---------------------------------------------------------------------------
// Ports (8 banners, one per port)
// ---------------------------------------------------------------------------

export type PortId =
  | 'cutlass_cove'
  | 'port_royale'
  | 'tortuga'
  | 'nassau'
  | 'charleston'
  | 'havana'
  | 'saint_domingue'
  | 'maracaibo';

const PORT_BANNERS: Record<PortId, string> = {
  cutlass_cove: '/art/ports/cutlass_cove.webp',
  port_royale: '/art/ports/port_royale.webp',
  tortuga: '/art/ports/tortuga.webp',
  nassau: '/art/ports/nassau.webp',
  charleston: '/art/ports/charleston.webp',
  havana: '/art/ports/havana.webp',
  saint_domingue: '/art/ports/saint_domingue.webp',
  maracaibo: '/art/ports/maracaibo.webp',
};

export function portBanner(id: string): string {
  return PORT_BANNERS[id as PortId] ?? PORT_BANNERS.cutlass_cove;
}

// ---------------------------------------------------------------------------
// Ships (5 classes)
// ---------------------------------------------------------------------------

export type ShipClass = 'sloop' | 'brigantine' | 'frigate' | 'galleon' | 'man_of_war';

const SHIP_IMAGES: Record<ShipClass, string> = {
  sloop: '/art/ships/sloop.webp',
  brigantine: '/art/ships/brigantine.webp',
  frigate: '/art/ships/frigate.webp',
  galleon: '/art/ships/galleon.webp',
  man_of_war: '/art/ships/man_of_war.webp',
};

export function shipImage(cls: string): string {
  return SHIP_IMAGES[cls as ShipClass] ?? SHIP_IMAGES.sloop;
}

// ---------------------------------------------------------------------------
// NPCs (7 tutorial NPCs)
// ---------------------------------------------------------------------------

export type NpcId =
  | 'old_pete'
  | 'mistress_vela'
  | 'drillmaster_bartolo'
  | 'sneaky_jim'
  | 'quartermaster_hale'
  | 'shipwright_mara'
  | 'harbormaster_finn';

const NPC_PORTRAITS: Record<NpcId, string> = {
  old_pete: '/art/npcs/old_pete.webp',
  mistress_vela: '/art/npcs/mistress_vela.webp',
  drillmaster_bartolo: '/art/npcs/drillmaster_bartolo.webp',
  sneaky_jim: '/art/npcs/sneaky_jim.webp',
  quartermaster_hale: '/art/npcs/quartermaster_hale.webp',
  shipwright_mara: '/art/npcs/shipwright_mara.webp',
  harbormaster_finn: '/art/npcs/harbormaster_finn.webp',
};

export function npcPortrait(id: string): string {
  return NPC_PORTRAITS[id as NpcId] ?? NPC_PORTRAITS.old_pete;
}

// ---------------------------------------------------------------------------
// Backgrounds (10 scene images)
// ---------------------------------------------------------------------------

export type SceneId =
  | 'tavern_interior'
  | 'quarterdeck'
  | 'captains_cabin'
  | 'dock_at_night'
  | 'training_yard'
  | 'shipyard'
  | 'jail_cell'
  | 'hospital'
  | 'marketplace'
  | 'nautical_chart';

const SCENE_IMAGES: Record<SceneId, string> = {
  tavern_interior: '/art/backgrounds/tavern_interior.webp',
  quarterdeck: '/art/backgrounds/quarterdeck.webp',
  captains_cabin: '/art/backgrounds/captains_cabin.webp',
  dock_at_night: '/art/backgrounds/dock_at_night.webp',
  training_yard: '/art/backgrounds/training_yard.webp',
  shipyard: '/art/backgrounds/shipyard.webp',
  jail_cell: '/art/backgrounds/jail_cell.webp',
  hospital: '/art/backgrounds/hospital.webp',
  marketplace: '/art/backgrounds/marketplace.webp',
  nautical_chart: '/art/backgrounds/nautical_chart.webp',
};

export function sceneImage(id: string): string {
  return SCENE_IMAGES[id as SceneId] ?? SCENE_IMAGES.dock_at_night;
}

// ---------------------------------------------------------------------------
// Textures (repeating patterns)
// ---------------------------------------------------------------------------

export const TEXTURES = {
  parchment: '/art/textures/parchment.webp',
  wood: '/art/textures/wood.webp',
  leather: '/art/textures/leather.webp',
  stone: '/art/textures/stone.webp',
  goldFrame: '/art/textures/gold_frame.webp',
} as const;

// ---------------------------------------------------------------------------
// Items (11 item icons)
// ---------------------------------------------------------------------------

export type ItemAssetId =
  | 'doubloon'
  | 'pirate_mark'
  | 'cutlass'
  | 'flintlock_pistol'
  | 'blunderbuss'
  | 'boarding_axe'
  | 'tankard_of_rum'
  | 'treasure_chest_closed'
  | 'treasure_chest_open'
  | 'brass_compass'
  | 'scroll';

const ITEM_IMAGES: Record<ItemAssetId, string> = {
  doubloon: '/art/items/doubloon.webp',
  pirate_mark: '/art/items/pirate_mark.webp',
  cutlass: '/art/items/cutlass.webp',
  flintlock_pistol: '/art/items/flintlock_pistol.webp',
  blunderbuss: '/art/items/blunderbuss.webp',
  boarding_axe: '/art/items/boarding_axe.webp',
  tankard_of_rum: '/art/items/tankard_of_rum.webp',
  treasure_chest_closed: '/art/items/treasure_chest_closed.webp',
  treasure_chest_open: '/art/items/treasure_chest_open.webp',
  brass_compass: '/art/items/brass_compass.webp',
  scroll: '/art/items/scroll.webp',
};

export function itemImage(id: string): string {
  return ITEM_IMAGES[id as ItemAssetId] ?? ITEM_IMAGES.doubloon;
}
